import { type BookingSearchType, bookingSearchPermissions } from "@nexxt/contracts/booking";
import { type CatalogSearchType, catalogSearchPermissions } from "@nexxt/contracts/catalog";
import { type PartySearchType, partySearchPermissions } from "@nexxt/contracts/party";
import { type ProcurementSearchType, procurementSearchPermissions } from "@nexxt/contracts/procurement";
import {
  type DatasetKey,
  datasetKeys,
  type ReferenceSearchType,
  referenceSearchPermissions,
} from "@nexxt/contracts/reference";
import { type SalesSearchType, salesSearchPermissions } from "@nexxt/contracts/sales";
import {
  Barcode,
  BookUser,
  CalendarDays,
  Contact,
  Database,
  Factory,
  FileSignature,
  KeyRound,
  Layers,
  type LucideIcon,
  Megaphone,
  ReceiptText,
  Shirt,
  ShoppingCart,
  Users,
} from "lucide-react";
import { m } from "../../../paraglide/messages.js";
import { datasetLabel } from "../reference/reference-texts";

// What the search finds, by domain and type (ADR-117). A domain enters with its types and the permission of each,
// from its contract: the same lists the server reads, so a type the user cannot read is not even asked.

export type SearchTypeName =
  | CatalogSearchType
  | ProcurementSearchType
  | SalesSearchType
  | PartySearchType
  | BookingSearchType
  | ReferenceSearchType;

/** The domain whose contract lists a type: the one that answers for it, with its request in `search.queries.ts`. */
type DomainOf<T extends SearchTypeName> = T extends CatalogSearchType
  ? "catalog"
  : T extends ProcurementSearchType
    ? "procurement"
    : T extends SalesSearchType
      ? "sales"
      : T extends PartySearchType
        ? "party"
        : T extends BookingSearchType
          ? "booking"
          : "reference";
export type SearchDomain = DomainOf<SearchTypeName>;

/** A domain that could not answer, named in a sentence ("Could not search the catalog"). */
export const searchDomainLabels: Record<SearchDomain, () => string> = {
  catalog: m.search_domain_catalog,
  procurement: m.search_domain_procurement,
  sales: m.search_domain_sales,
  party: m.search_domain_party,
  booking: m.search_domain_booking,
  reference: m.search_domain_reference,
};

const searchPermissions: Record<SearchTypeName, string> = {
  ...catalogSearchPermissions,
  ...procurementSearchPermissions,
  ...salesSearchPermissions,
  ...partySearchPermissions,
  ...bookingSearchPermissions,
  ...referenceSearchPermissions,
};

/** What a hit carries that the app may say better than the domain. */
interface HitFacts {
  subtitle: string | null;
  parentId: string | null;
}

interface SearchTypeView<D extends SearchDomain> {
  domain: D;
  /** The heading of its group, in ⌘K and on the search page. */
  label: () => string;
  icon: LucideIcon;
  /** What an archived hit of the type is called (a cancelled PO). */
  archived: () => string;
  /** The line under the title when the domain has none to give: the dataset of a reference record, in words. */
  subtitle?: (hit: HitFacts) => string | null;
}

/** The dataset a reference record opens in: the parent of its hit. */
export const datasetOf = (parentId: string | null): DatasetKey | undefined =>
  datasetKeys.find((key) => key === parentId);

/** In the order the groups show when no code says otherwise; a type's domain is the one of its contract. */
export const searchTypes: { [T in SearchTypeName]: SearchTypeView<DomainOf<T>> } = {
  sales_order: { domain: "sales", label: m.nav_sales_orders, icon: ReceiptText, archived: m.so_status_cancelled },
  purchase_order: {
    domain: "procurement",
    label: m.nav_purchase_orders,
    icon: ShoppingCart,
    archived: m.po_status_cancelled,
  },
  linesheet: { domain: "catalog", label: m.nav_linesheets, icon: Layers, archived: m.linesheet_archived_badge },
  product: { domain: "catalog", label: m.nav_products, icon: Shirt, archived: m.linesheet_archived_badge },
  sku: { domain: "catalog", label: m.product_skus, icon: Barcode, archived: m.linesheet_archived_badge },
  organization: { domain: "party", label: m.nav_organizations, icon: BookUser, archived: m.filter_archived },
  contact: { domain: "party", label: m.organization_tab_contacts, icon: Contact, archived: m.filter_archived },
  etaf: { domain: "party", label: m.nav_etaf, icon: FileSignature, archived: m.filter_archived },
  manufacturer: { domain: "party", label: m.nav_manufacturers, icon: Factory, archived: m.filter_archived },
  sales_campaign: { domain: "booking", label: m.nav_sales_campaigns, icon: Megaphone, archived: m.filter_archived },
  appointment: {
    domain: "booking",
    label: m.nav_appointments,
    icon: CalendarDays,
    archived: m.booking_status_cancelled,
  },
  user: { domain: "party", label: m.nav_users, icon: Users, archived: m.user_status_disabled },
  role: { domain: "party", label: m.nav_roles, icon: KeyRound, archived: m.filter_archived },
  reference_record: {
    domain: "reference",
    label: m.nav_reference,
    icon: Database,
    archived: m.reference_field_archived,
    subtitle: (hit) => {
      const dataset = datasetOf(hit.parentId);
      return dataset === undefined ? null : datasetLabel[dataset]();
    },
  },
};

/** The line under the title of a hit: the domain's, or the one the app says for its type. */
export const subtitleOf = (hit: HitFacts & { type: SearchTypeName }): string | null =>
  hit.subtitle ?? searchTypes[hit.type].subtitle?.(hit) ?? null;

/** The types in the order of their groups; also the `type` the search page accepts. */
export const typeOrder = Object.keys(searchTypes) as SearchTypeName[];

/** The types the user can read, in their order; only `type`, when there is one. */
export const readableSearchTypes = (permissions: readonly string[], type?: SearchTypeName) =>
  typeOrder.filter((t) => permissions.includes(searchPermissions[t]) && (type === undefined || t === type));
